import React, { useEffect, useState } from "react";
import api, { resolveImg } from "@/lib/api";
import PhotoManager from "@/components/PhotoManager";
import TagInput from "@/components/TagInput";
import MarketingPreferences from "@/components/MarketingPreferences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Building2, Save, Check, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", website: "", phone: "", email: "", address: "", city: "", state: "",
  zip_code: "", logo: "", description: "", brands_carried: [],
};

export default function DealershipProfile() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/dealership").then(({ data }) => setForm({ ...EMPTY, ...data })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/dealership", form);
      toast.success("Dealership profile updated");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading profile…</div>;

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="dealership-profile-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-navy">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your dealership profile, branding and account. This branding is applied to every campaign you generate.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white rounded-2xl border border-border p-6 space-y-5">
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-xl bg-secondary border-2 border-border overflow-hidden flex items-center justify-center shrink-0">
              {form.logo ? <img src={resolveImg(form.logo)} alt="logo" className="w-full h-full object-cover" data-testid="logo-preview" /> : <Building2 className="w-8 h-8 text-muted-foreground" />}
            </div>
            <div className="flex-1">
              <Label className="mb-2 block">Dealership Logo</Label>
              <PhotoManager photos={form.logo ? [form.logo] : []} onChange={(p) => setForm((f) => ({ ...f, logo: p[p.length - 1] || "" }))} testidPrefix="logo" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Dealership Name</Label><Input required value={form.name} onChange={set("name")} data-testid="profile-name" /></div>
            <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={set("website")} placeholder="https://…" data-testid="profile-website" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="profile-phone" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="profile-email" /></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 space-y-5">
          <h2 className="font-serif text-lg font-bold text-navy">Location</h2>
          <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="profile-address" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} data-testid="profile-city" /></div>
            <div className="space-y-1.5"><Label>State</Label><Input value={form.state} onChange={set("state")} data-testid="profile-state" /></div>
            <div className="space-y-1.5"><Label>ZIP Code</Label><Input value={form.zip_code} onChange={set("zip_code")} data-testid="profile-zip" /></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 space-y-5">
          <h2 className="font-serif text-lg font-bold text-navy">Branding</h2>
          <div className="space-y-1.5"><Label>Short Description</Label><Textarea value={form.description} onChange={set("description")} rows={3} data-testid="profile-description" /></div>
          <div className="space-y-1.5"><Label>Brands Carried</Label><TagInput tags={form.brands_carried} onChange={(t) => setForm((f) => ({ ...f, brands_carried: t }))} placeholder="e.g. Boston Whaler - press Enter" testid="profile-brands" /></div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy-light text-white h-11 px-6" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Save Profile
          </Button>
        </div>
      </form>

      <MarketingPreferences />

      <div className="bg-white rounded-2xl border border-border p-6 space-y-3" data-testid="settings-team">
        <h2 className="font-serif text-lg font-bold text-navy">Users / Team Members</h2>
        <p className="text-sm text-muted-foreground">Invite teammates and manage who can create campaigns for your dealership.</p>
        <span className="inline-block text-xs font-semibold text-navy bg-secondary px-3 py-1.5 rounded-full">Team management coming soon</span>
      </div>

      <div className="bg-white rounded-2xl border border-border p-6 space-y-6" data-testid="settings-billing">
        <div>
          <h2 className="font-serif text-lg font-bold text-navy">Subscription &amp; Billing</h2>
          <p className="text-sm text-muted-foreground">Preview our dealership membership plans. Billing is not active yet.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* DEALER */}
          <div className="relative rounded-2xl border-2 border-border bg-white p-6 flex flex-col" data-testid="plan-dealer">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Dealer</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-navy">$299</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">The core plan for individual boat dealerships.</p>
            </div>
            <ul className="space-y-2.5 flex-1">
              {[
                "AI-powered marine marketing campaign generation",
                "Multiple creative concepts per campaign",
                "Static social ad graphics",
                "Story and Reel creative",
                "Vertical social video",
                "Facebook and Instagram ad copy",
                "Organic social captions",
                "Email marketing copy",
                "SMS marketing copy",
                "Campaign Library",
                "Marketing Opportunities",
                "Marketing Calendar",
                "Manual boat inventory management",
                "Dealer branding and logo support",
                "Downloadable PNG and video creatives",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-navy/90">
                  <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              onClick={() => toast.info("Online subscription enrollment coming soon")}
              className="mt-6 w-full bg-navy hover:bg-navy-light text-white h-11 font-bold"
              data-testid="choose-dealer-btn"
            >
              Choose Dealer
            </Button>
          </div>

          {/* DEALER PRO */}
          <div className="relative rounded-2xl border-2 border-gold bg-white p-6 flex flex-col ring-2 ring-gold/20 shadow-lg shadow-gold/10" data-testid="plan-dealer-pro">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 gold-gradient text-navy text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow" data-testid="most-popular-badge">
              <Star className="w-3 h-3" /> Most Popular
            </span>
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">Dealer Pro</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-navy">$399</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Everything in Dealer, plus advanced features coming soon.</p>
            </div>
            <div className="text-xs font-semibold uppercase tracking-widest text-navy mb-2">Everything in Dealer, plus</div>
            <ul className="space-y-2.5 flex-1">
              {[
                "DMS inventory synchronization",
                "Expanded team and user access",
                "Higher campaign and video usage",
                "Advanced Marketing Opportunities",
                "Priority campaign generation",
                "Future social media publishing",
                "Future Meta and Google advertising integrations",
                "Expanded automation features",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-navy/90">
                  <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <span>{f}</span>
                  <span className="text-[10px] font-semibold text-muted-foreground bg-secondary px-1.5 py-0.5 rounded ml-auto shrink-0 self-center">Soon</span>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              onClick={() => toast.info("Online subscription enrollment coming soon")}
              className="mt-6 w-full gold-gradient text-navy h-11 font-bold gold-sheen"
              data-testid="choose-dealer-pro-btn"
            >
              Choose Dealer Pro
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center" data-testid="billing-note">
          Advertising spend, if applicable in the future, will be separate from the monthly platform subscription.
        </p>
      </div>
    </div>
  );
}
